import React from 'react';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import type {SettingsStackParamList} from '@/app/routes';
import {useAuthStore} from '@/entities/session/auth-store';
import {NotificationSettingsForm} from '@/features/manage-notification-settings/ui/NotificationSettingsForm';
import {CompanyTerminalSettingsForm} from '@/features/manage-terminal-settings/ui/CompanyTerminalSettingsForm';
import {Page} from '@/shared/ui/page';
import {SettingsNav} from '@/pages/settings/SettingsNav';
import {Section} from '@/shared/ui/section';

/**
 * "Организация" → Дашборд → Настройки уведомлений. The company-wide kill
 * switch for Telegram arrival/departure pings, the absence digest and the daily
 * report — sits next to Telegram recipients but edits the sending policy, not
 * who receives.
 *
 * The stranger-logging switch is here too: it decides whether an unrecognised
 * pass becomes a notification at all, so it belongs with the other answers to
 * "what gets sent", not on a settings screen of its own.
 */
type Props = NativeStackScreenProps<SettingsStackParamList, 'NotificationSettings'>;

export function NotificationSettingsScreen(_: Props) {
  const {t} = useTranslation();
  const companyId = useAuthStore(s => s.activeContext?.companyId);

  return (
    <Page
      sideNav={<SettingsNav />}
      width="form"
      scroll
      testID="company-notif-settings"
      title={t('notifSettings.title')}
      >
      {companyId ? <NotificationSettingsForm companyId={companyId} /> : null}

      {/* Lives here, not on a screen of its own: it decides whether an
          unrecognised pass becomes a log entry and a Telegram message at all,
          which is the same question the switches above answer for known people. */}
      {companyId ? (
        <Section title={t('notifSettings.terminalTitle')}>
          <CompanyTerminalSettingsForm companyId={companyId} />
        </Section>
      ) : null}
    </Page>
  );
}
